import React from 'react';
import { Head, Link } from '@inertiajs/react';
import AdminLayout from '../../../components/AdminLayout';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Plus, Edit, Eye, Trash2 } from 'lucide-react';

interface User {
  id: number;
  email_address: string;
  role: string;
  created_at: string;
  updated_at: string;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
}

interface Props {
  users: User[];
  pagination: Pagination;
}

const UsersIndex: React.FC<Props> = ({ users, pagination }) => {
  return (
    <AdminLayout title="Gestion des utilisateurs">
      <Head title="Utilisateurs - Admin" />
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Utilisateurs</h1>
            <p className="text-gray-600">
              {pagination.total_count} utilisateur{pagination.total_count > 1 ? 's' : ''} au total
            </p>
          </div>
          <Link href="/admin/users/new">
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Nouvel utilisateur
            </Button>
          </Link>
        </div>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle>Liste des utilisateurs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-900">ID</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Email</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Rôle</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Créé le</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Modifié le</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-900">#{user.id}</td>
                      <td className="py-3 px-4 text-sm text-gray-900">{user.email_address}</td>
                      <td className="py-3 px-4 text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.role === 'admin' 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {new Date(user.updated_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <Link href={`/admin/users/${user.id}`}>
                            <Button size="sm" variant="ghost">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Link href={`/admin/users/${user.id}/edit`}>
                            <Button size="sm" variant="ghost">
                              <Edit className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Link 
                            href={`/admin/users/${user.id}`} 
                            method="delete" 
                            as="button"
                            preserveScroll
                            onBefore={() => {
                              if (!confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
                                return false;
                              }
                            }}
                          >
                            <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-800">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Page {pagination.current_page} sur {pagination.total_pages}
                </div>
                <div className="flex space-x-2">
                  {pagination.current_page > 1 && (
                    <Link href={`/admin/users?page=${pagination.current_page - 1}`}>
                      <Button variant="outline" size="sm">
                        Précédent
                      </Button>
                    </Link>
                  )}
                  {pagination.current_page < pagination.total_pages && (
                    <Link href={`/admin/users?page=${pagination.current_page + 1}`}>
                      <Button variant="outline" size="sm">
                        Suivant
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default UsersIndex; 
